import { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { digObject, sortArrayBy } from 'functions/utilities'

import defaultUserState, { requiredFields as defaultRequiredFields } from 'models/userWithActable'

import useForm from 'hooks/useForm'
import useThunkDispatch from 'hooks/useThunkDispatch'
import useWatchEntityUpdates from 'hooks/useWatchEntityUpdates'

import useLatestEntity from 'agency/hooks/useLatestEntity'

import * as userActions from 'agency/redux/modules/user'

import {
  isAdmin, isAgencyAdmin, isAgencyUser, isBrandUser, isMasterAdmin, isProvider,
} from 'functions/user'

// IMPORTANT - Make sure you have added a PageContext.Provider
// to the page that contains this hook

import PageContext from 'contexts/pageContext'

const watchEntityKeys = [
  'integrations',
]

const generateAuthenticatedAppUrl = (params) => {
  const {
    user, application_key, hash, dispatch, path,
  } = params

  const { generateAuthenticatedAppUrl: generateFn } = userActions
  return dispatch(generateFn(user, { application_key, hash, path }))
}

const updateUsername = (entityState, setEntityState, onBlurEntityState) => {
  const { email, username } = entityState

  onBlurEntityState('email')

  if (!username){
    setEntityState({ email, username: email })
  }
}

const createUser = (userParams, dispatch, requestOptions) => {
  const { createUser: createFn } = userActions
  const { association_id, association_type } = userParams

  const updatedParams = { ...userParams }

  if (association_id && association_type === 'Agency'){
    updatedParams.agency_id = association_id.toString()
  }

  return dispatch(createFn(updatedParams, requestOptions))
}

const updateUser = (user, userParams, dispatch, requestOptions) => {
  const { updateUser: updateFn } = userActions
  const {
    association_id, association_type, assigned_agent_ids, custom_permissions, options,
  } = userParams

  const updatedParams = {
    ...userParams,
    assigned_agent_ids: JSON.stringify(assigned_agent_ids),
    custom_permissions: JSON.stringify(custom_permissions),
    id: user.id,
  }

  if (association_id && association_type === 'Agency'){
    updatedParams.agency_id = association_id.toString()
  }

  if (options){
    const userOptions = user.options || {}
    updatedParams.options = JSON.stringify({
      ...JSON.parse(JSON.stringify(userOptions)),
      ...options,
    })
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

const deleteUser = (user, dispatch) => {
  const { deleteUser: deleteFn } = userActions
  return dispatch(deleteFn(user))
}

const transferCampaigns = (user, newAgentId, dispatch) => {
  const { transferCampaigns: transferFn } = userActions
  return dispatch(transferFn(user, newAgentId))
}

const deleteAgent = (user, dispatch, showDeleteAgentModal) => new Promise((resolve, reject) => {
  if (showDeleteAgentModal){
    const payload = {
      callbacks: {
        deleteUser: () => deleteUser(user, dispatch),
        transferCampaigns: newAgentId => transferCampaigns(user, newAgentId, dispatch),
      },
      user,
    }

    showDeleteAgentModal(payload)

    return resolve({ success: true, result: payload })
  }

  return reject(new Error('showDeleteAgentModal not defined in PageContext callbacks'))
})

const editEbuAssignedAgents = (user, dispatch, showAssignEbuAgentsModal) => new Promise((resolve, reject) => {
  if (showAssignEbuAgentsModal){
    const payload = {
      callbacks: {
        updateUser: (updatedParams, requestOptions) => updateUser(user, updatedParams, dispatch, requestOptions),
      },
      user,
    }

    showAssignEbuAgentsModal(payload)

    return resolve({ success: true, result: payload })
  }

  return reject(new Error('showAssignEbuAgentsModal not defined in PageContext callbacks'))
})

export function useUserForm(initUser, customRequiredFields = []){
  const { entity: user } = useLatestEntity(initUser, 'users')
  const dispatch = useThunkDispatch()

  const requiredFields = [...defaultRequiredFields, ...customRequiredFields]

  const actable = digObject(user, 'actable', {})

  const userForm = useForm(defaultUserState, { entity: { ...user, ...actable }, requiredFields }, [user.id, actable.id])
  const { entityState, setEntityState, onBlurEntityState } = userForm

  return {
    ...userForm,
    callbacks: {
      createUser: (userParams, requestOptions) => createUser(userParams, dispatch, requestOptions),
      updateUser: (userParams, requestOptions) => updateUser(user, userParams, dispatch, requestOptions),
      updateUsername: () => updateUsername(entityState, setEntityState, onBlurEntityState),
    },
  }
}

export function useRelations(user){
  const {
    association_id,
    association_type,
    image: imageId,
    role_id,
  } = user

  const entities = useSelector(reduxState => reduxState.entities)

  const {
    images,
    providers,
    roles,
  } = entities

  const firstImageId = imageId || digObject(user, 'images', [])[0]
  const provider = association_type === 'Provider' && providers[association_id] ? providers[association_id] : {}
  const role = role_id && roles[role_id] ? roles[role_id] : {}

  const image = images[firstImageId] || {}

  return {
    entities,
    image,
    provider,
    role,
  }
}

function useUser(initUser){
  const { entity: user } = useLatestEntity(initUser, 'users')

  const {
    updatedEntities: { integrations: integrationsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const { showAssignEbuAgentsModal, showDeleteAgentModal } = callbacks || {}

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.users)

  const entities = useSelector(reduxState => reduxState.entities)
  const { integrations } = entities

  const relations = useRelations(user)
  const { image, provider, role } = relations

  const roleTitle = role.title

  const filteredIntegrations = useMemo(() => {
    const array = Object.values(integrations)
    const filtered = array.filter((integration) => {
      const { integrable_type, integrable_id } = integration
      return integrable_type === 'User' && integrable_id === user.id
    })
    const sorted = sortArrayBy(filtered, 'asc', 'service_provider')

    return sorted
  }, [user.id, integrationsUpdatedAt])

  return {
    actable: user.actable || {},
    callbacks: {
      createUser: (userParams, requestOptions) => createUser(userParams, dispatch, requestOptions),
      deleteAgent: () => deleteAgent(user, dispatch, showDeleteAgentModal),
      deleteUser: () => deleteUser(user, dispatch),
      editEbuAssignedAgents: () => editEbuAssignedAgents(user, dispatch, showAssignEbuAgentsModal),
      generateAuthenticatedAppUrl: ({ application_key, hash, path }) => (
        generateAuthenticatedAppUrl({
          application_key, dispatch, hash, path, user,
        })
      ),
      updateUser: (updatedParams, requestOptions) => updateUser(user, updatedParams, dispatch, requestOptions),
    },
    creating,
    deleting,
    filteredIntegrations,
    image,
    isAdmin: isAdmin(roleTitle),
    isAgencyAdmin: isAgencyAdmin(roleTitle),
    isAgencyUser: isAgencyUser(roleTitle),
    isAgent: user.actable_type === 'Agent',
    isBrandUser: isBrandUser(roleTitle),
    isMasterAdmin: isMasterAdmin(roleTitle),
    isOfficeAdministrator: user.actable_type === 'OfficeAdministrator',
    isProviderDirector: isProvider(roleTitle),
    isProviderUser: isProvider(roleTitle),
    loading,
    provider,
    roleTitle,
    updating,
    user,
  }
}

export default useUser
